import { useEffect, useState } from "react";
import API from "../lib/api";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { BellRinging, Play } from "@phosphor-icons/react";

const LINES = [
  { key: "amber", label: "Amber — half way through SLA (50%)", dot: "bg-amber-500" },
  { key: "red", label: "Red — three quarters through SLA (75%)", dot: "bg-red-500" },
  { key: "breached", label: "SLA breached (100%)", dot: "bg-red-700" },
];

/* Settings → Emergency Escalation: which SLA ladder lines push an alert to admins. */
export default function SlaAlertSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    API.get("/scheduler/sla-alerts/settings").then(({ data }) => setCfg(data)).catch(() => setCfg({ enabled: true, lines: { amber: true, red: true, breached: true } }));
  }, []);

  const save = async (next) => {
    setCfg(next);
    try {
      const { data } = await API.put("/scheduler/sla-alerts/settings", next);
      setCfg(data);
      toast.success("SLA alert settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await API.post("/scheduler/sla-alerts/run-now");
      toast.success(data.raised ? `${data.raised} alert(s) sent` : "Nothing new to alert — every crossed line has already been sent");
    } catch { toast.error("Check failed"); }
    finally { setRunning(false); }
  };

  if (!cfg) return null;
  return (
    <div className="border border-border rounded-lg p-4 space-y-3" data-testid="sla-alert-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BellRinging size={16} weight="duotone" className="text-amber-600" />
          <div>
            <p className="text-sm font-medium">SLA ladder alerts</p>
            <p className="text-[10px] text-muted-foreground">Push admins the moment an active emergency crosses a line. Stops once the crew arrives on site. Checked every minute.</p>
          </div>
        </div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ ...cfg, enabled: v })} data-testid="sla-alerts-enabled" />
      </div>
      {cfg.enabled && (
        <div className="grid sm:grid-cols-3 gap-2">
          {LINES.map((l) => (
            <label key={l.key} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs cursor-pointer" data-testid={`sla-alert-line-${l.key}`}>
              <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${l.dot}`} /> {l.label}</span>
              <Switch checked={cfg.lines?.[l.key] !== false} onCheckedChange={(v) => save({ ...cfg, lines: { ...cfg.lines, [l.key]: v } })} className="scale-90" data-testid={`sla-alert-line-${l.key}-switch`} />
            </label>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={runNow} disabled={running} data-testid="sla-alerts-run-now">
          <Play size={12} className="mr-1" /> {running ? "Checking…" : "Run check now"}
        </Button>
      </div>
    </div>
  );
}
